// TypeFields.jsx

import React from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Hooks
import useTypeFieldsFieldReorder from './hooks/useTypeFieldsFieldReorder';
import useTypeFieldsResolvedMeta from './hooks/useTypeFieldsResolvedMeta';
import useFieldModalController from 'node/form/hooks/useFieldModalController';
import useNodeFormFields from 'node/form/hooks/useNodeFormFields';
import useNodeTypeDefinition from 'node/form/hooks/useNodeTypeDefinition';
import useNodeTypeFieldMutations from 'node/form/hooks/useNodeTypeFieldMutations';
import useNodeTypeSchemaCacheRefresh from 'node/form/hooks/useNodeTypeSchemaCacheRefresh';
import useOverrideFields from 'node/form/hooks/useOverrideFields';

// Constants
import { FIELD_MESSAGES } from 'node/form/constants/fieldMessages';

// CSS
import './TypeFields.css';
import './TypeFieldsModal.css';

// Components
import TypeFieldsFieldList from 'collection/typefields/fieldlist/TypeFieldsFieldList';
import FieldModal from 'node/form/fieldmodal/FieldModal';
/**
 * TypeFields
 *
 * Embeddable component that renders type-level field management with add, edit, delete,
 * and reorder support. Can be used standalone inside a modal shell (TypeFieldsModal)
 * or embedded directly into a wizard or other surface.
 *
 * @param {string} typeKey - Node type key to manage.
 * @param {object|null} typeDef - Fallback node type definition when live definition is unavailable.
 * @param {string|null} baseKey - Optional base key override.
 *
 * @returns {ReactNode} The rendered type properties content.
 */
const TypeFields = React.memo(({
  typeKey,
  typeDef: fallbackTypeDef,
  baseKey,
}) => {

  // ----- CONTEXT ----- //

  // Language Context

  const language = useCurrentLanguage();

  // Node Types Context
  const { getType, getRevision } = useNodeTypes();

  const nodeTypeKey = String(typeKey || '').trim();

  const nodeTypesRevision = getRevision();

  // ----- HOOKS ----- //

  // Live node type definition for the selected type key.

  const { typeDef: liveTypeDef, loading } = useNodeTypeDefinition(nodeTypeKey, getType, nodeTypesRevision);

  // Resolved node type definition from live data or fallback.
  const typeDef = liveTypeDef || fallbackTypeDef || null;

  // Indicates whether selected type is system-managed and non-editable.
  const isSystemType = typeDef?.system === true;

  // Field override state for local reordering and edits.
  const { overrideFields, setOverrideFields } = useOverrideFields({
    nodeTypeKey,
    typeDef,
  });

  // Resolved fields for rendering and field modal usage.
  const { fields, resolvedFields } = useNodeFormFields({
    nodeTypeKey,
    language,
    typeDef,
    overrideFields,
  });

  // Field modal state and actions.
  const {
    fieldModalOpen,
    fieldInsertIndex,
    fieldEditingField,
    handleFieldModalOpen,
    handleAddFieldClick,
    handleSettingsClick,
  } = useFieldModalController({ canEdit: !isSystemType });

  const {
    resolvedBaseKey,
    resolvedTypeKey,
    nodeTypeLabel,
  } = useTypeFieldsResolvedMeta({
    nodeTypeKey,
    typeDef,
    baseKey,
  });

  // Cache refresh helper for node type schema updates.
  const refreshNodeTypesCache = useNodeTypeSchemaCacheRefresh();

  // Node type field mutation actions.
  const {
    fieldSaving,
    handleFieldSave,
    handleFieldUpdate,
    handleFieldDelete,
  } = useNodeTypeFieldMutations({
    isDisabled: isSystemType,
    fieldEditingField,
    fieldInsertIndex,
    fields,
    resolvedFields,
    overrideFields,
    setOverrideFields,
    resolvedTypeKey,
    resolvedBaseKey,
    getNodeKey: () => null,
    requireNodeKey: false,
    typeNotReadyMessage: FIELD_MESSAGES.NODE_TYPE_NOT_READY,
    refreshNodeTypesCache,
    closeFieldModal: () => handleFieldModalOpen(false),
  });

  // Field reorder drag state and handlers.
  const {
    draggingFieldName,
    dragOverFieldName,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDrop,
  } = useTypeFieldsFieldReorder({
    fields,
    resolvedFields,
    overrideFields,
    setOverrideFields,
    resolvedTypeKey,
    resolvedBaseKey,
    refreshNodeTypesCache,
    isSystemType,
  });

  // ----- RENDER ----- //

  return (
    <>
      <div className="type-fields-container">

        {/* EMPTY */}
        {loading ? (
          <div
            className="type-fields-empty"
          >
            Loading fields...
          </div>
        ) : (
          <>
            <TypeFieldsFieldList
              fields={fields}
              draggingFieldName={draggingFieldName}
              dragOverFieldName={dragOverFieldName}
              isSystemType={isSystemType}
              onAddFieldClick={handleAddFieldClick}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDragStart={handleDragStart}
              onDrop={handleDrop}
              onSettingsClick={handleSettingsClick}
            />
          </>
        )}

      </div>

      <FieldModal
        isOpen={fieldModalOpen}
        setOpen={handleFieldModalOpen}
        isSaving={fieldSaving}
        editingField={fieldEditingField}
        nodeTypeLabel={nodeTypeLabel}
        existingFields={resolvedFields}
        typeKey={nodeTypeKey}
        baseKey={resolvedBaseKey}
        onDelete={handleFieldDelete}
        onSave={fieldEditingField ? handleFieldUpdate : handleFieldSave}
      />
    </>
  );
});

// Dev Tools
TypeFields.displayName = 'TypeFields';

export default TypeFields;
